import React from 'react';

interface ProgressBarProps {
  progress?: number; // 0-100
  status?: 'processing' | 'completed' | 'stopped' | 'error';
  totalModels?: number;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress = 45,
  status = 'processing',
  totalModels = 0
}) => {
  const getStatusMessage = () => {
    switch (status) {
      case 'processing':
        // Use actual totalModels count instead of calculating from progress
        const modelCount = totalModels || Math.ceil(progress / 33);
        return `Processing your prompt across ${modelCount} model${modelCount !== 1 ? 's' : ''}...`;
      case 'completed':
        return 'All models have completed processing';
      case 'stopped':
        return 'Processing has been stopped';
      case 'error':
        return 'Some models encountered errors';
      default:
        return 'Processing...';
    }
  };

  const getProgressColor = () => {
    switch (status) {
      case 'completed':
        return 'bg-gradient-to-r from-green-400 to-green-500';
      case 'stopped':
        return 'bg-slate-500';
      case 'error':
        return 'bg-gradient-to-r from-red-400 to-red-500';
      default:
        return 'bg-gradient-to-r from-cyan-400 to-blue-500';
    }
  };

  return (
    <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl px-8 py-8">
      <div className="flex items-center justify-between gap-8">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <h2
              className="text-xl font-semibold text-white"
              id="processing-status"
            >
              {getStatusMessage()}
            </h2>
            <div className="flex items-center gap-3">
              <span
                className="text-2xl font-bold text-cyan-300"
                aria-label={`${Math.round(progress)} percent complete`}
              >
                {Math.round(progress)}%
              </span>
              <div className="text-sm text-slate-400">complete</div>
            </div>
          </div>
          <div
            className="w-full bg-slate-700/50 rounded-full h-3 shadow-inner"
            role="progressbar"
            aria-valuenow={Math.round(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-labelledby="processing-status"
          >
            <div
              className={`h-3 rounded-full transition-all duration-700 ease-out ${getProgressColor()} shadow-sm`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="mt-3 text-sm text-slate-300">
            {status === 'processing' && 'Models are working on your prompt...'}
            {status === 'completed' && 'All models have finished processing.'}
            {status === 'stopped' && 'You can edit your prompt and restart.'}
            {status === 'error' && 'Some models failed, but you can continue with successful ones.'}
          </div>
        </div>
        <div className="flex-shrink-0 hidden lg:flex items-center justify-center">
          {/* Enhanced processing graphic */}
          <div className="w-20 h-20 bg-slate-900/50 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-slate-600/50">
            {status === 'processing' && (
              <div
                className="w-10 h-10 border-4 border-cyan-400/80 border-t-transparent rounded-full animate-spin"
                aria-hidden="true"
              ></div>
            )}
            {status === 'completed' && (
              <div className="text-green-400 text-2xl">✓</div>
            )}
            {status === 'stopped' && (
              <div className="text-slate-400 text-2xl">⏸</div>
            )}
            {status === 'error' && (
              <div className="text-red-400 text-2xl">⚠</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};